import { projectAudienceSelectionFromProject } from '#shared/types/Project'
import {
    getAvailableVolunteeringWeekSpots,
    hasAvailableVolunteeringWeekSpots,
    normalizeVolunteeringWeekCapacityCount
} from '#shared/utils/volunteeringWeekCapacity'
import { formatSlovakRegionLabel } from '@/utils/slovakRegion'

type SerializableDate =
    | string
    | number
    | Date
    | null
    | undefined
    | {
          toDate?: () => Date
          _seconds?: number
          seconds?: number
      }

export type VolunteeringWeekOpportunity = {
    id: string
    slug?: string
    title?: string
    image?: string | null
    coverImg?: string | null
    orgTitle?: string
    orgName?: string
    orgImage?: string | null
    orgMeta?: {
        image?: string | null
        title?: string | null
    } | null
    geoCity?: string
    geoRegion?: string
    remote?: boolean
    how?: string[]
    when?: string
    startDateAt?: SerializableDate
    endDateAt?: SerializableDate
    maxVolunteerCount?: string | number | null
    receivedVolunteerRequestsCount?: string | number | null
    availableSpots?: string | number | null
    location?: {
        lat?: number
        lng?: number
    } | null
    who?: string[]
    whoGroupEnabled?: boolean
    requiresProfessionalExperience?: boolean | null
    createdAt?: SerializableDate
    updatedAt?: SerializableDate
}

export function getVolunteeringWeekOpportunityTitle(project: VolunteeringWeekOpportunity): string {
    return sanitizeText(project.title) || 'Dobrovoľnícka príležitosť'
}

export function getVolunteeringWeekOpportunityOrganizationName(project: VolunteeringWeekOpportunity): string {
    return sanitizeText(project.orgTitle) || sanitizeText(project.orgName) || sanitizeText(project.orgMeta?.title)
}

export function getVolunteeringWeekOpportunityImageUrl(project: VolunteeringWeekOpportunity): string {
    return project.image || project.coverImg || project.orgMeta?.image || project.orgImage || ''
}

export function getVolunteeringWeekOpportunityAudience(project: VolunteeringWeekOpportunity): string[] {
    return projectAudienceSelectionFromProject({
        who: project.who ?? [],
        whoGroupEnabled: project.whoGroupEnabled
    })
}

export function getVolunteeringWeekOpportunityHowValues(project: VolunteeringWeekOpportunity): string[] {
    const values = new Set<string>()

    ;(project.how ?? []).forEach((value) => {
        const normalizedValue = String(value || '').trim().toLowerCase()
        if (normalizedValue) values.add(normalizedValue)
    })

    if (project.remote) {
        values.add('remotely')
    }

    return [...values]
}

export function getVolunteeringWeekOpportunityHowLabel(value: string): string {
    const normalizedValue = String(value || '').trim().toLowerCase()

    if (normalizedValue === 'remotely' || normalizedValue === 'online') return 'Online'
    if (normalizedValue === 'in_person') return 'Osobne'
    if (normalizedValue === 'hybrid') return 'Online aj osobne'

    return value
}

export function getVolunteeringWeekOpportunityParticipationModes(project: VolunteeringWeekOpportunity): string[] {
    const howValues = getVolunteeringWeekOpportunityHowValues(project)
    const modes = new Set<string>()

    if (howValues.some((mode) => ['remotely', 'online', 'hybrid'].includes(mode))) {
        modes.add('online')
    }

    if (howValues.some((mode) => ['in_person', 'hybrid'].includes(mode))) {
        modes.add('in_person')
    }

    return [...modes]
}

export function isVolunteeringWeekOpportunityProfessional(project: VolunteeringWeekOpportunity): boolean {
    return Boolean(project.requiresProfessionalExperience)
}

export function isVolunteeringWeekOpportunityOnline(project: VolunteeringWeekOpportunity): boolean {
    return getVolunteeringWeekOpportunityHowValues(project).some((mode) => ['remotely', 'online', 'hybrid'].includes(mode))
}

export function getVolunteeringWeekOpportunityLocationLabel(project: VolunteeringWeekOpportunity): string {
    const region = normalizeRegionLabel(project.geoRegion)
    const city = sanitizeText(project.geoCity)
    return [region, city].filter(Boolean).join(', ')
}

export function getVolunteeringWeekOpportunityDateLabel(project: VolunteeringWeekOpportunity): string {
    const startDate = parseVolunteeringWeekOpportunityDate(project.startDateAt)

    if (startDate) {
        return startDate.toLocaleDateString('sk-SK', {
            day: 'numeric',
            month: 'long'
        })
    }

    const when = sanitizeText(project.when)
    if (when) return when

    return 'Termín neuvedený'
}

export function getVolunteeringWeekOpportunityFullDateLabel(
    project: Pick<VolunteeringWeekOpportunity, 'startDateAt' | 'when'>
): string {
    const startDate = parseVolunteeringWeekOpportunityDate(project.startDateAt)

    if (startDate) {
        return formatFullDate(startDate)
    }

    const when = sanitizeText(project.when)
    if (when) return when

    return 'Termín neuvedený'
}

export function getVolunteeringWeekOpportunityDateKey(project: VolunteeringWeekOpportunity): string {
    const startDate = parseVolunteeringWeekOpportunityDate(project.startDateAt)
    if (!startDate) return ''

    return [
        startDate.getFullYear(),
        String(startDate.getMonth() + 1).padStart(2, '0'),
        String(startDate.getDate()).padStart(2, '0')
    ].join('-')
}

export function getVolunteeringWeekOpportunityRecommendedSortTime(project: VolunteeringWeekOpportunity): number {
    return (
        parseVolunteeringWeekOpportunityDate(project.updatedAt)?.getTime()
        ?? parseVolunteeringWeekOpportunityDate(project.createdAt)?.getTime()
        ?? parseVolunteeringWeekOpportunityDate(project.startDateAt)?.getTime()
        ?? 0
    )
}

export function getVolunteeringWeekOpportunityTimeRangeLabel(project: VolunteeringWeekOpportunity): string {
    const startDate = parseVolunteeringWeekOpportunityDate(project.startDateAt)
    if (!startDate) return ''

    const endDate = parseVolunteeringWeekOpportunityDate(project.endDateAt)
    if (!endDate) return formatTime(startDate)

    return `${formatTime(startDate)} - ${formatTime(endDate)}`
}

export function getVolunteeringWeekOpportunityScheduleLabel(
    project: Pick<VolunteeringWeekOpportunity, 'startDateAt' | 'endDateAt' | 'when'>
): string {
    const startDate = parseVolunteeringWeekOpportunityDate(project.startDateAt)
    if (!startDate) {
        const when = sanitizeText(project.when)
        return when || 'Termín neuvedený'
    }

    const endDate = parseVolunteeringWeekOpportunityDate(project.endDateAt)
    const startDateLabel = formatFullDate(startDate)
    const startTime = formatTime(startDate)

    if (!endDate) {
        return `${startDateLabel} od ${startTime}`
    }

    if (isSameDay(startDate, endDate)) {
        return `${startDateLabel} od ${startTime} - ${formatTime(endDate)}`
    }

    return `${startDateLabel} od ${startTime} - ${formatFullDate(endDate)} do ${formatTime(endDate)}`
}

export function getVolunteeringWeekOpportunityFreeSpotsLabel(project: VolunteeringWeekOpportunity): string {
    const spots = getAvailableVolunteeringWeekSpots(project)
    const maxVolunteerCount = normalizeVolunteeringWeekCapacityCount(project.maxVolunteerCount)

    if (spots !== null && maxVolunteerCount !== null) {
        return `Voľných miest: ${spots} z ${maxVolunteerCount}`
    }

    if (spots !== null) {
        return `Voľných miest: ${spots}`
    }

    if (maxVolunteerCount !== null) {
        return `Voľných miest: 0 z ${maxVolunteerCount}`
    }

    return 'Voľných miest: 0'
}

export function hasVolunteeringWeekOpportunityFreeSpots(project: VolunteeringWeekOpportunity): boolean {
    return hasAvailableVolunteeringWeekSpots(project)
}

export function parseVolunteeringWeekOpportunityDate(value: SerializableDate): Date | null {
    if (!value) return null
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value

    if (typeof value === 'object' && typeof value?.toDate === 'function') {
        const date = value.toDate()
        return Number.isNaN(date.getTime()) ? null : date
    }

    if (typeof value === 'number') {
        const timestamp = value < 1000000000000 ? value * 1000 : value
        const date = new Date(timestamp)
        return Number.isNaN(date.getTime()) ? null : date
    }

    if (typeof value === 'string') {
        const maybeTimestamp = Number(value)
        if (!Number.isNaN(maybeTimestamp)) {
            const timestamp = maybeTimestamp < 1000000000000 ? maybeTimestamp * 1000 : maybeTimestamp
            const date = new Date(timestamp)
            return Number.isNaN(date.getTime()) ? null : date
        }

        const date = new Date(value)
        return Number.isNaN(date.getTime()) ? null : date
    }

    const seconds = value._seconds ?? value.seconds
    if (typeof seconds === 'number' && Number.isFinite(seconds)) {
        const date = new Date(seconds * 1000)
        return Number.isNaN(date.getTime()) ? null : date
    }

    return null
}

export function normalizeRegionLabel(value: unknown): string {
    return formatSlovakRegionLabel(value)
}

function sanitizeText(value: unknown): string {
    return typeof value === 'string' ? value.trim() : ''
}

function formatTime(date: Date): string {
    return date.toLocaleTimeString('sk-SK', {
        hour: '2-digit',
        minute: '2-digit'
    })
}

function formatFullDate(date: Date): string {
    return date.toLocaleDateString('sk-SK', {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric'
    })
}

function isSameDay(left: Date, right: Date): boolean {
    return (
        left.getFullYear() === right.getFullYear()
        && left.getMonth() === right.getMonth()
        && left.getDate() === right.getDate()
    )
}
